import { useState, useEffect, useCallback } from 'react';
import { useHydratedTime } from './useHydratedTime';

const UNITS = [
    ['year', 365 * 24 * 60 * 60],
    ['month', 30 * 24 * 60 * 60],
    ['week', 7 * 24 * 60 * 60],
    ['day', 24 * 60 * 60],
    ['hour', 60 * 60],
    ['minute', 60],
] as const;

function formatFullRelative(diffSeconds: number) {
    const isFuture = diffSeconds > 0;
    let remaining = Math.abs(diffSeconds);

    if (remaining < 60) return 'now';

    const parts: string[] = [];

    for (const [unit, secondsPerUnit] of UNITS) {
        const value = Math.floor(remaining / secondsPerUnit);
        if (value > 0) {
            parts.push(`${value} ${unit}${value !== 1 ? 's' : ''}`);
            remaining -= value * secondsPerUnit;
        }
    }

    if (parts.length === 0) return 'now';

    let formatted = '';

    if (parts.length === 1) {
        formatted = parts[0];
    } else {
        formatted =
            parts.slice(0, -1).join(' ') + ' and ' + parts[parts.length - 1];
    }

    return isFuture ? `in ${formatted}` : `${formatted} ago`;
}

export function useRelativetime(dateString: string | undefined) {
    const { getHydratedNow } = useHydratedTime();

    const getRelative = useCallback(
        (now: Date) => {
            if (!dateString) return '';

            const date = new Date(dateString);
            if (Number.isNaN(date.getTime())) return '';

            const diffSeconds = Math.round(
                (date.getTime() - now.getTime()) / 1000,
            );
            return formatFullRelative(diffSeconds);
        },
        [dateString],
    );

    const [label, setLabel] = useState('');

    useEffect(() => {
        if (!getHydratedNow) return;

        const update = () => setLabel(getRelative(getHydratedNow()));
        update();

        const id = setInterval(update, 60_000);
        return () => clearInterval(id);
    }, [getRelative, getHydratedNow]);

    return label;
}
